import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '@/utils/api';

export interface User {
    uuid: string;
    userName: string;
    userEmail: string;
    user_active: boolean;
    user_pause: boolean;
    role?: {
        role_name: string;
    };
}

interface UsersContextType {
    users: User[];
    activeUsers: User[];
    loading: boolean;
    error: Error | null;
    refreshUsers: () => Promise<void>;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

export const UsersProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('📡 UsersContext: Fetching users...');
            const response = await api.req({
                url: '/users?limit=100',
                method: api.Method.GET,
            });

            if (response.status === 'success' && response.data) {
                const data = response.data as any;
                const usersArray = (data && typeof data === 'object' && 'users' in data) ? data.users : [];
                setUsers(usersArray);
                console.log('✅ UsersContext: Users loaded:', usersArray.length);
            }
        } catch (err) {
            console.error('❌ UsersContext: Failed to fetch users:', err);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    };

    // Load users on mount (only once)
    useEffect(() => {
        loadUsers();
    }, []);

    // Computed property: active users
    const activeUsers = users.filter(u => u.user_active === true && u.user_pause === false);

    const refreshUsers = async () => {
        await loadUsers();
    };

    return (
        <UsersContext.Provider value={{ users, activeUsers, loading, error, refreshUsers }}>
            {children}
        </UsersContext.Provider>
    );
};

// Custom hook to use the users context
export const useUsers = () => {
    const context = useContext(UsersContext);
    if (context === undefined) {
        throw new Error('useUsers must be used within a UsersProvider');
    }
    return context;
};
